import { css } from '@emotion/react';
import React from 'react';

import { AmendmentConfirmationHeader } from '@/booking-amendment';
import {
  ActivitiesCardSection,
  AsyncPaymentConfirmationHeader,
  BookingConfirmationDetails,
  BookingConfirmationHeader,
  RestaurantsCardSection,
} from '@/booking-confirmation';
import { MembershipConfirmationCard } from '@/booking-membership/components/MembershipConfirmationCard';
import { useCurrentBookingMembershipDiscountDetail } from '@/booking-membership/hooks/use-current-booking-membership-discount-detail.hook';
import { useBookingPaymentProviderDetails } from '@/booking-payment/hooks/use-booking-payment-provider-details.hook';
import { Box, Flex } from '@/box';
import { useActiveBrandConfig } from '@/brand';
import ResponsiveConditional from '@/common/components/responsive/ResponsiveConditional.component';
import LargeLoadingIndicator from '@/core/components/controls/LargeLoadingIndicator.component';
import Card from '@/core/components/layout/Card.component';
import { useHotel } from '@/hotel';
import { WhereToFindUs } from '@/hotel/components/WhereToFindUs.component';
import { useHotelParking } from '@/hotel/hooks/use-hotel-parking.hook';
import { useLocale, useTranslation } from '@/i18n';
import { TownhouseBanner } from '@/promotions';
import { Spacer, Stack } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';

import { AnyQuestionsCard } from '../components/AnyQuestionsCard.component';
import { GleneaglesServiceChargeCard } from '../components/GleneaglesServiceChargeCard.component';
import { ThirdPartyPaymentDownloadCard } from '../components/confirmation/ThirdPartyPaymentDownloadCard.component';
import { useBooking, useIsBookingPendingConfirmation } from '../hooks';
import { BookingRequest } from '../interfaces';

interface BookingConfirmationContainerInterface {
  bookingRequest: BookingRequest;
  oldBooking?: BookingRequest;
}

export const BookingConfirmationContainer: React.FC<
  BookingConfirmationContainerInterface
> = ({ bookingRequest, oldBooking }) => {
  const { offersThirdPartyAuthorisationForm } = useActiveBrandConfig();

  const { baseName: language } = useLocale();

  const { t } = useTranslation('bookingMembership');

  const booking = useBooking(bookingRequest);
  const hotel = useHotel(bookingRequest.hotelSlug);
  const isPendingConfirmation = useIsBookingPendingConfirmation(bookingRequest);
  const { data: paymentDetails } = useBookingPaymentProviderDetails({
    bookingId: bookingRequest.bookingId,
    hotelReferenceId: bookingRequest.hotelSlug,
  });

  const hotelParkingDetails = useHotelParking(bookingRequest.hotelSlug);

  const {
    membershipPrice,
    includesBundledMembership: showConfirmationCard,
    discountDetail,
  } = useCurrentBookingMembershipDiscountDetail();

  const {
    __temporary: { displayCarTransportOnNewLine },
    componentProperties: { confirmation },
  } = useTheme();

  if (!booking || !hotel) {
    // TODO: Return a loading screen...
    return <LargeLoadingIndicator maxHeight={100} />;
  }

  const {
    content: { images, restaurants, activities },
    contact: { emails },
    urls,
    location: { transport },
  } = hotel;

  const transportOptions = displayCarTransportOnNewLine
    ? transport.filter((option) => option.mode !== 'PARKING')
    : transport;

  const parkingTitle = displayCarTransportOnNewLine
    ? hotelParkingDetails?.title
    : undefined;
  const parkingText = displayCarTransportOnNewLine
    ? hotelParkingDetails?.description
    : undefined;

  const renderHeader = () => {
    if (oldBooking) {
      return <AmendmentConfirmationHeader bookingRequest={bookingRequest} />;
    }
    if (isPendingConfirmation) {
      return <AsyncPaymentConfirmationHeader bookingRequest={bookingRequest} />;
    }
    return <BookingConfirmationHeader bookingRequest={bookingRequest} />;
  };

  return (
    <main>
      {renderHeader()}
      <div
        css={css({
          margin: '-300px auto auto auto',
          maxWidth: '1280px',
          position: 'relative',
          zIndex: 1000,
          ...confirmation?.bookingConfirmationContainer,
          ['@media all and (max-width: 730px)']:
            confirmation?.bookingConfirmationContainer?.mobile,
        })}
      >
        {/* Move this into brand config once the new format is in place */}
        {hotel.referenceId === 'gleneagles' && <TownhouseBanner />}
        <div
          css={css({
            backgroundColor: 'white',
            padding: '32px',
            ...confirmation?.bookingConfirmationAdditionalDetailsContainer,
            ['@media all and (max-width: 730px)']: {
              padding: '28px',
              ...confirmation?.bookingConfirmationAdditionalDetailsContainer
                ?.mobile,
            },
          })}
        >
          {showConfirmationCard && membershipPrice && (
            <MembershipConfirmationCard
              amountSaved={discountDetail.amountSaved.value}
              currency={hotel.payments.currencyCode}
              email={booking.firstRoomStay.billerProfile.email}
              firstName={booking.firstRoomStay.billerProfile.firstName}
              lastName={booking.firstRoomStay.billerProfile.lastName}
              membershipPrice={membershipPrice.value}
              paymentMethod={t('confirmation.last4Digits', {
                last4Digits: paymentDetails?.metadata?.CardLast4 ?? '****',
              })}
              language={language}
            />
          )}
          <Flex flexWrap={['wrap']}>
            <Box flex={1} marginInlineEnd={[0, 16]}>
              <BookingConfirmationDetails
                bookingRequest={bookingRequest}
                isAmendedBooking={Boolean(oldBooking)}
                isPendingConfirmation={isPendingConfirmation}
              />
            </Box>
            <Box marginInlineStart={[0, 16]} width={[0, 1 / 2]}>
              <ResponsiveConditional
                desktop={<img src={images.hero?.source} width="100%" alt="" />}
              />
            </Box>
          </Flex>
        </div>
      </div>
      <Spacer
        css={{
          height: confirmation?.rowGap,
          '@media all and (max-width: 730px)': {
            height: confirmation?.mobile?.rowGap,
          },
        }}
        s="xl"
      />
      <div
        css={css`
          max-width: 1280px;
          margin: auto;
        `}
      >
        <Stack
          css={{
            rowGap: confirmation?.rowGap,
            '@media all and (max-width: 730px)': {
              rowGap: confirmation?.mobile?.rowGap,
            },
          }}
          s="xl"
        >
          {offersThirdPartyAuthorisationForm && emails.info && (
            <ThirdPartyPaymentDownloadCard
              destinationEmailAddress={emails.info}
            />
          )}
          {emails.info && (
            <AnyQuestionsCard
              email={emails.info}
              phone={hotel.contact.phone}
              faqUrl={urls.faq}
              // Very temporary solution until we build our block-based layout approach
              after={
                ['gleneagles', 'townhouse'].includes(hotel.referenceId) && (
                  <GleneaglesServiceChargeCard />
                )
              }
              css={{
                ...confirmation?.anyQuestionsCard,
                '@media all and (max-width: 730px)':
                  confirmation?.anyQuestionsCard?.mobile,
              }}
            />
          )}
          {activities.length > 0 && (
            <ActivitiesCardSection
              activities={activities}
              // Don't bother that it is using a restaurant title, Activities got merged into restaurants.
              title={hotel.content.sectionTitles?.restaurant}
            />
          )}
          {restaurants.length > 0 && (
            <RestaurantsCardSection
              restaurants={restaurants}
              title={hotel.content.sectionTitles?.restaurant}
            />
          )}

          <Card
            css={{
              ...confirmation?.whereToFindUsCard,
              ['@media all and (max-width: 730px)']: {
                ...confirmation?.whereToFindUsCard?.mobile,
              },
            }}
          >
            <WhereToFindUs
              mapImageUrl={images.map?.source}
              googleMapsUrl={urls.map}
              transportOptions={transportOptions}
              parkingTitle={parkingTitle}
              parkingText={parkingText}
            />
          </Card>
        </Stack>
      </div>
    </main>
  );
};
